'use client'

import type { QueryKey } from '@tanstack/react-query'
import type { GridFilterModel, GridSortModel } from '@mui/x-data-grid-pro'
import { useServerGrid, type ServerGridParams } from '../useServerGrid/useServerGrid'

/** Parameters passed to the queryFn for each grid state change. */
export type ServerGridQueryParams = ServerGridParams

export interface UseServerGridQueryOptions<TData, TRow> {
    /** Base query-key segments (ids, scope). Grid params are appended automatically. */
    queryKey: QueryKey
    queryFn: (params: ServerGridQueryParams) => Promise<TData>
    selectRows: (data: TData | undefined) => TRow[]
    selectTotal: (data: TData | undefined) => number
    enabled?: boolean
    initialPageSize?: number
    initialSortModel?: GridSortModel
    initialFilterModel?: GridFilterModel
    /** Controlled filter model — when provided, filter state lives outside the hook
     * and changes are reported through `onFilterModelChange` (page still resets to 0). */
    filterModel?: GridFilterModel
    onFilterModelChange?: (model: GridFilterModel) => void
    /** Debounce sort/filter/search-driven refetches by this many ms (pagination stays immediate). */
    debounceMs?: number
    pageSizeOptions?: number[]
    /** Server-side sorting: wires sortModel/sortingMode into dataGridProps (default true). */
    serverSorting?: boolean
    /** Server-side filtering: wires filterModel/filterMode into dataGridProps (default true). */
    serverFiltering?: boolean
}

/**
 * @deprecated Use `useServerGrid` with a generated `getXxxOptions(...)` factory — it takes
 * the generated query key/fn instead of a hand-written fetcher and query key.
 *
 * Kept as a thin adapter over `useServerGrid` (same grid state, reset-to-page-0, debounce,
 * keepPreviousData and `dataGridProps`) for callers whose endpoints aren't in the generated client yet.
 * Query key layout is unchanged: `[...queryKey, page, pageSize, sortJson, search, filterJson]`.
 */
export function useServerGridQuery<TData, TRow = unknown>({
    queryKey,
    queryFn,
    selectRows,
    selectTotal,
    ...gridOptions
}: UseServerGridQueryOptions<TData, TRow>) {
    return useServerGrid<TData, TRow>({
        ...gridOptions,
        query: (params) => ({
            queryKey: [...queryKey, params.page, params.pageSize, JSON.stringify(params.sortModel), params.search, JSON.stringify(params.filterModel)],
            queryFn: () => queryFn(params),
        }),
        select: (data) => ({ rows: selectRows(data), total: selectTotal(data) }),
    })
}
